// @material-ui core components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Chip from "@mui/material/Chip";
import Autocomplete from "@mui/material/Autocomplete";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Settings page components
import FormField from "components/account/FormField";

// Data
import selectData from "../../../data/account/selectData";

import { useContext, useState, useEffect } from "react";
import { LanguageContext } from "context";
import { useSessionContext } from "context/SessionContext";
import { useNotification } from "context/NotificationContext";

import { updateUserInfos } from "@shared/api/userInfo";

function BasicInfo({
  profile,
  setProfile,
}: {
  profile: any;
  setProfile: (p: any) => void;
}): JSX.Element {
  const { t } = useContext(LanguageContext) as { t: (k: string) => string };
  const { refresh, session } = useSessionContext();
  const { showNotification } = useNotification();

  const [form, setForm] = useState({
    nickname: "",
    title: "",
    first_name: "",
    last_name: "",
    gender: null as number | null,
    birth_year: null as string | null,
    birth_month: null as string | null, // "January" などのキーを保持
    birth_day: null as string | null,
    prefectures: "",
    phone: "",
    lang: "",
    hobby: [] as string[],
    mbti: "",
  });

  useEffect(() => {
    if (profile) {
      let birthYear = null;
      let birthMonth = null;
      let birthDay = null;

      if (profile.birth_date) {
        const localDate = new Date(profile.birth_date);
        const yyyy = localDate.getFullYear().toString();
        const mmIndex = localDate.getMonth(); // 0-11
        const dd = localDate.getDate().toString().padStart(2, "0");

        birthYear = yyyy;
        birthMonth = selectData.birthDate[mmIndex]; // キーとして保持
        birthDay = dd;
      }

      setForm({
        nickname: profile.nickname || "",
        title: profile.title || "",
        first_name: profile.first_name || "",
        last_name: profile.last_name || "",
        gender: typeof profile.gender === "number" ? profile.gender : null,
        birth_year: birthYear,
        birth_month: birthMonth,
        birth_day: birthDay,
        prefectures: profile.prefectures || "",
        phone: profile.phone || "",
        lang: profile.lang || "",
        hobby: profile.hobby ? profile.hobby.split(",") : [],
        mbti: profile.mbti || "",
      });
    }
  }, [profile]);

  const handleChange = (e: any) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleUpdate = async () => {
    try {
      let birthdate: string | null = null;

      if (form.birth_year && form.birth_month && form.birth_day) {
        const yyyy = form.birth_year;
        // キーから数値に変換（例："January" -> 1 -> "01"）
        const monthIndex = selectData.birthDate.indexOf(form.birth_month) + 1;
        const mm = monthIndex.toString().padStart(2, "0");
        const dd = form.birth_day.padStart(2, "0");

        const isoDate = `${yyyy}-${mm}-${dd}`;
        const date = new Date(`${isoDate}T00:00:00`);

        if (!isNaN(date.getTime())) {
          birthdate = isoDate;
        }
      }
      
      if (!session?.access_token) {
        await refresh();
      }
      const params = {
        ...form,
        birthdate,
        hobby: form.hobby.join(","),
      };

      const updated = await updateUserInfos(session!.access_token, params);
      setProfile(updated);

      showNotification(
        {
          icon: "check_circle",
          title: t("Common.success"),
          message: t("Common.updated-success"),
        },
        2000
      );
      console.log(`update basicInfo success`);
    } catch (err) {
      console.error("更新エラー", err);
      showNotification(
        {
          icon: "error",
          title: t("Common.error"),
          message: t("Common.updated-error"),
        },
        2000
      );
    }
  };

  return (
    <Card id="basic-info" sx={{ overflow: "visible" }}>
      <MDBox p={3}>
        <MDTypography variant="h5">{t("Sttting-BasicInfo.auth")}</MDTypography>
      </MDBox>
      <MDBox component="form" pb={3} px={3}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <FormField
              name="nickname"
              label={t("Sttting-BasicInfo.nickname")}
              placeholder={t("Sttting-BasicInfo.nickname-ph")}
              value={form.nickname}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormField
              name="title"
              label={t("Sttting-BasicInfo.position")}
              placeholder={t("Sttting-BasicInfo.position-ph")}
              value={form.title}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormField
              name="last_name"
              label={t("Sttting-BasicInfo.last-name")}
              placeholder={t("Sttting-BasicInfo.last--ph")}
              value={form.last_name}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormField
              name="first_name"
              label={t("Sttting-BasicInfo.first-name")}
              placeholder={t("Sttting-BasicInfo.first-ph")}
              value={form.first_name}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={3}>
                <Autocomplete
                  value={form.gender !== null ? selectData.gender[form.gender] : null}
                  options={selectData.gender}
                  getOptionLabel={(option) => t(`Form-gender.${option}`) || option}
                  isOptionEqualToValue={(option, value) => String(option) === String(value)}
                  onChange={(_, value) => {
                    if (value === null) {
                      setForm({ ...form, gender: null });
                    } else {
                      const genderIndex = selectData.gender.indexOf(value);
                      setForm({ ...form, gender: genderIndex });
                    }
                  }}
                  renderInput={(params) => (
                    <FormField {...params} name="gender" label={t("Sttting-BasicInfo.male-ph")} />
                  )}
                  clearOnEscape
                />
              </Grid>
              <Grid item xs={12} sm={9}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={3}>
                    <Autocomplete
                      value={selectData.years.includes(form.birth_year!) ? form.birth_year : null}
                      options={selectData.years}
                      isOptionEqualToValue={(option, value) => String(option) === String(value)}
                      onChange={(_, value) => {
                        setForm({ ...form, birth_year: value ?? null });
                      }}
                      renderInput={(params) => (
                        <FormField
                          {...params}
                          name="birth_year"
                          label={t("Sttting-BasicInfo.birthday-year")}
                        />
                      )}
                      clearOnEscape
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Autocomplete
                      value={selectData.birthDate.includes(form.birth_month!) ? form.birth_month : null}
                      options={selectData.birthDate}
                      getOptionLabel={(option) => t(`Form-monthly.${option}`) || option}
                      isOptionEqualToValue={(option, value) => String(option) === String(value)}
                      onChange={(_, value) => {
                        setForm({ ...form, birth_month: value ?? null });
                      }}
                      renderInput={(params) => (
                        <FormField
                          {...params}
                          name="birth_month"
                          label={t("Sttting-BasicInfo.birthday-month")}
                        />
                      )}
                      clearOnEscape
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Autocomplete
                      value={selectData.days.includes(form.birth_day!) ? form.birth_day : null}
                      options={selectData.days}
                      isOptionEqualToValue={(option, value) => String(option) === String(value)}
                      onChange={(_, value) => {
                        setForm({ ...form, birth_day: value ?? null });
                      }}
                      renderInput={(params) => (
                        <FormField
                          {...params}
                          name="birth_day"
                          label={t("Sttting-BasicInfo.birthday-day")}
                        />
                      )}
                      clearOnEscape
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormField
              name="prefectures"
              label={t("Sttting-BasicInfo.location")}
              placeholder={t("Sttting-BasicInfo.location-ph")}
              value={form.prefectures}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormField
              name="phone"
              label={t("Sttting-BasicInfo.phone")}
              placeholder={t("Sttting-BasicInfo.phone-ph")}
              inputProps={{ type: "number" }}
              value={form.phone}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Autocomplete
              value={form.lang || null}
              options={["日本語", "English"]}
              isOptionEqualToValue={(option, value) => option === value}
              onChange={(_, value) => {
                setForm((prevForm) => ({ ...prevForm, lang: value ?? "" }));
              }}
              renderInput={(params) => (
                <FormField
                  {...params}
                  label={t("Sttting-BasicInfo.language")}
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Autocomplete
              multiple
              value={form.hobby}
              options={selectData.hobby}
              getOptionLabel={(option) => t(`Form-hobby.${option}`) || option}
              isOptionEqualToValue={(option, value) => String(option) === String(value)}
              onChange={(_, value) => setForm({ ...form, hobby: value })}              
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={index}
                    label={t(`Form-hobby.${option}`) || option}
                    sx={{
                      background: "linear-gradient(195deg, #49a3f1, #1A73E8) !important",
                      color: "#ffffff !important", // 文字色も白を強制
                      borderRadius: "8px !important",
                      border: "none !important",
                      "& .MuiChip-deleteIcon": {
                        color: "rgba(255, 255, 255, 0.8) !important",
                        "&:hover": {
                          color: "#ffffff !important",
                        },
                      },
                    }}
                  />
                ))
              }
              renderInput={(params) => (
                <FormField
                  {...params}
                  name="hobby"
                  label={t("Sttting-BasicInfo.hobby")}
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6} sx={{ marginBottom: "0px", paddingBottom: "0px" }}>
            <Autocomplete
              value={selectData.mbti.includes(form.mbti) ? form.mbti : null}
              options={selectData.mbti}
              isOptionEqualToValue={(option, value) => String(option) === String(value)}
              onChange={(_, value) => setForm({ ...form, mbti: value || "" })}
              renderInput={(params) => (
                <FormField
                  {...params}
                  name="mbti"
                  label="MBTI"
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sx={{ marginTop: "-12px" }}>
            <MDBox mt={0} display="flex" justifyContent="flex-end">
              <MDButton variant="gradient" color="info" size="small" onClick={handleUpdate}>
                {t("Sttting-BasicInfo.update")}
              </MDButton>
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>
    </Card>
  );
}

export default BasicInfo;